import { parseAssetCode } from "./const";
// Helper functions to normalize transaction data (works with both formats)
function normalizeAssetCode(value) {
    // Some API fields may include "stellar:CODE:issuer", "issuer:CODE" or
    // "CODE:issuer"; a supported code in any position wins.
    return parseAssetCode(value) || null;
}
/** Get amount_in value as string */
export function getAmountIn(tx) {
    var _a;
    if (typeof tx.amount_in === "object" && ((_a = tx.amount_in) === null || _a === void 0 ? void 0 : _a.amount)) {
        return tx.amount_in.amount;
    }
    return tx.amount_in || "0";
}
/** Get amount_out value as string */
export function getAmountOut(tx) {
    var _a;
    if (typeof tx.amount_out === "object" && ((_a = tx.amount_out) === null || _a === void 0 ? void 0 : _a.amount)) {
        return tx.amount_out.amount;
    }
    return tx.amount_out || "0";
}
/**
 * Fee mode the wallet/backend already recorded on the transaction, if any.
 * Returns undefined for an absent or unrecognised value so callers fall back
 * to the exact_in default.
 */
export function getFeeMode(tx) {
    if (tx.fee_mode === "exact_out" || tx.fee_mode === "exact_in") {
        return tx.fee_mode;
    }
    return undefined;
}
/**
 * Fee mode becomes immutable once a withdrawal payment exists and the user has
 * payment instructions. A default fee_mode alone does not mean it is locked.
 */
export function getIsFeeModeLocked(tx) {
    var _a;
    return Boolean(tx.kind === "withdrawal" &&
        (((_a = tx.rozo) === null || _a === void 0 ? void 0 : _a.payment_id) || tx.withdraw_memo || tx.withdraw_anchor_account));
}
/**
 * The amount the user actually asked for. Under exact_out that is amount_out
 * (what the recipient must receive); otherwise it is amount_in.
 */
export function getRequestedAmount(tx) {
    if (getFeeMode(tx) === "exact_out") {
        var out = getAmountOut(tx);
        if (out && out !== "0") {
            return out;
        }
    }
    return getAmountIn(tx);
}
/** Get fee amount as string */
export function getAmountFee(tx) {
    var _a;
    if ((_a = tx.fee_details) === null || _a === void 0 ? void 0 : _a.total) {
        return tx.fee_details.total;
    }
    return tx.amount_fee || "0";
}
/** Get amount_in asset */
export function getAmountInAsset(tx) {
    var _a;
    if (typeof tx.amount_in === "object" && ((_a = tx.amount_in) === null || _a === void 0 ? void 0 : _a.asset)) {
        return tx.amount_in.asset;
    }
    return tx.amount_in_asset || tx.asset_code || "USDC";
}
/** Get amount_out asset */
export function getAmountOutAsset(tx) {
    var _a;
    if (typeof tx.amount_out === "object" && ((_a = tx.amount_out) === null || _a === void 0 ? void 0 : _a.asset)) {
        return tx.amount_out.asset;
    }
    return tx.amount_out_asset || tx.asset_code || "USDC";
}
/** Get fee asset */
export function getFeeAsset(tx) {
    var _a;
    if ((_a = tx.fee_details) === null || _a === void 0 ? void 0 : _a.asset) {
        return tx.fee_details.asset;
    }
    return tx.amount_fee_asset || tx.asset_code || "USDC";
}
/**
 * Best-effort deposit token code resolver.
 * Used to decide which token/logo to show for deposit flows.
 */
export function getDepositTokenCode(tx) {
    var _a, _b, _c, _d, _e;
    // Prefer explicit SEP-24 fields
    var fromSep24 = (_a = normalizeAssetCode(tx.amount_in_asset)) !== null && _a !== void 0 ? _a : normalizeAssetCode(tx.asset_code);
    if (fromSep24)
        return fromSep24;
    // Rozo-specific fields (newer backend/pusher payloads)
    var fromRozo = (_c = normalizeAssetCode((_b = tx.rozo) === null || _b === void 0 ? void 0 : _b.display_currency)) !== null && _c !== void 0 ? _c : normalizeAssetCode((_d = tx.rozo) === null || _d === void 0 ? void 0 : _d.destination_token_symbol);
    if (fromRozo)
        return fromRozo;
    // As a last resort, try amount_in nested asset
    if (typeof tx.amount_in === "object") {
        return normalizeAssetCode((_e = tx.amount_in) === null || _e === void 0 ? void 0 : _e.asset);
    }
    return null;
}
/** Get user's Stellar account */
export function getUserAccount(tx) {
    // For withdrawals, 'from' is the user's account in new format
    if (tx.kind === "withdrawal" && tx.from) {
        return tx.from;
    }
    // For deposits, 'to' is the user's account in new format
    if (tx.kind === "deposit" && tx.to) {
        return tx.to;
    }
    // Fall back to old format
    return tx.account || "";
}
export function getFromAddress(tx) {
    return tx.from || "";
}
/** Get destination address (for withdrawals) */
export function getToAddress(tx) {
    return tx.to || tx.to_address || "";
}
/** Get withdraw memo (the memo user must attach when paying anchor) */
export function getWithdrawMemo(tx) {
    var _a;
    // New SEP-24 format
    if (tx.withdraw_memo) {
        return tx.withdraw_memo;
    }
    // Old format stored in rozo.source_memo
    if ((_a = tx.rozo) === null || _a === void 0 ? void 0 : _a.source_memo) {
        return tx.rozo.source_memo;
    }
    return tx.memo || null;
}
/** Get withdraw anchor account (where user sends Stellar payment) */
export function getWithdrawAnchorAccount(tx) {
    var _a;
    // New SEP-24 format
    if (tx.withdraw_anchor_account) {
        return tx.withdraw_anchor_account;
    }
    // Old format stored in rozo.source_receiving_address
    return ((_a = tx.rozo) === null || _a === void 0 ? void 0 : _a.source_receiving_address) || null;
}
